import React, { forwardRef, ReactNode } from "react";
import {
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableCellProps,
	TableContainer,
	TableHead,
	TableProps,
	TableRow,
	TableRowProps,
	TableSortLabel,
	Tooltip,
	Typography,
} from "@mui/material";
import { CorePagination, CoreSpinner, CoreTableNoResultsIcon, TableSortLabelIcon } from "@verity/components/core";
import { getReactNodeText } from "../../../../packages/components/src/util";
import styles from "./composite-table.module.css";
import i18n from "../i18n";

export type CompositeTableHeaderColumn<Key extends string, SortableKey extends Key> = (
	| { key: SortableKey; sortable: true }
	| {
			key: Key;
			sortable?: false;
	  }
) & {
	content: ReactNode;
} & Omit<TableCellProps, "variant" | "children" | "content">;

export default function CompositeTable({
	head,
	children,
	...tableProps
}: {
	head: ReactNode;
} & TableProps) {
	return (
		<Table {...tableProps}>
			<TableHead className={styles.tableHeader}>{head}</TableHead>
			<TableBody>{children}</TableBody>
		</Table>
	);
}

export function CompositeTableContainer({ className, children }: { className?: string; children: ReactNode }) {
	return (
		<TableContainer
			tabIndex={0}
			className={`${styles.tableWrapper} ${className || ""}`}
			onKeyDown={(e) => {
				if (e.key === "ArrowUp") {
					e.currentTarget.scrollTop -= 50;
					e.preventDefault();
				} else if (e.key === "ArrowDown") {
					e.currentTarget.scrollTop += 50;
					e.preventDefault();
				}
			}}
		>
			{children}
		</TableContainer>
	);
}

function CompositeTableHeaderCell({ className, children, ...tableCellProps }: Omit<TableCellProps, "variant">) {
	return (
		<TableCell {...tableCellProps} variant="head" className={`${styles.tableHeaderCell} ${className || ""}`}>
			{children}
		</TableCell>
	);
}

type SortDirection = "asc" | "desc";

export function CompositeTableHeaderRow<
	Key extends string,
	SortableKey extends Key,
	SortRequired extends boolean | undefined,
>({
	columns,
	sortRequired,
	sortBy,
	onSortByChange,
}: {
	columns: CompositeTableHeaderColumn<Key, SortableKey>[];
	sortRequired?: SortRequired;
} & (
	| {
			sortBy: { key: SortableKey; direction: SortDirection }[];
			onSortByChange: (sort: { key: SortableKey; direction: SortDirection }[]) => void;
	  }
	| (SortRequired extends true
			? {
					sortBy: { key: SortableKey; direction: SortDirection };
					onSortByChange: (sort: { key: SortableKey; direction: SortDirection }) => void;
				}
			: {
					sortBy?: { key: SortableKey; direction: SortDirection };
					onSortByChange?: (sort?: { key: SortableKey; direction: SortDirection }) => void;
				})
)) {
	const sortDirections: Partial<Record<SortableKey, SortDirection>> = {};
	if (sortBy) {
		if (Array.isArray(sortBy)) {
			sortBy.forEach(({ key, direction }) => {
				sortDirections[key] = direction;
			});
		} else {
			sortDirections[sortBy.key] = sortBy.direction;
		}
	}

	return (
		<TableRow>
			{columns.map(({ key, content, sortable, width, sx, ...tableCellProps }) => {
				let headerContent: ReactNode = content;
				const sortDirection = sortable ? sortDirections[key] : undefined;
				if (onSortByChange && sortable) {
					// `content` is a ReactNode, but the tooltip title and aria-label need a string. Trailing trim
					// covers columns with no extractable text (and locales that haven't picked up the
					// interpolation yet), so the label reads "Sort" rather than "Sort ".
					const sortLabel = (
						i18n.t("components.compositeTableTSX.sortTooltip", { columnName: getReactNodeText(content) }) as string
					).trim();
					headerContent = (
						<Tooltip title={sortLabel}>
							<TableSortLabel
								aria-label={sortLabel}
								active={!!sortDirection}
								direction={sortDirection}
								onClick={() => {
									if (Array.isArray(sortBy)) {
										const onMultiSortChange = onSortByChange as (
											sort: {
												key: SortableKey;
												direction: SortDirection;
											}[],
										) => void;
										if (sortDirection) {
											const setSortDirection = (newDirection: SortDirection) =>
												onMultiSortChange(
													sortBy.map((sort) =>
														sort.key === key
															? {
																	key,
																	direction: newDirection,
																}
															: sort,
													),
												);

											if (sortDirection === "asc") {
												setSortDirection("desc");
											} else if (sortRequired && sortBy.length === 1) {
												setSortDirection("asc");
											} else {
												onMultiSortChange(sortBy.filter((sort) => sort.key !== key));
											}
										} else {
											onMultiSortChange(sortBy.concat([{ key, direction: "asc" }]));
										}
									} else {
										const onSingleSortChange = onSortByChange as (sort?: {
											key: SortableKey;
											direction: SortDirection;
										}) => void;
										if (sortDirection) {
											if (sortDirection === "asc") {
												onSingleSortChange({ key, direction: "desc" });
											} else if (sortRequired) {
												onSingleSortChange({ key, direction: "asc" });
											} else {
												onSingleSortChange(undefined);
											}
										} else {
											onSingleSortChange({ key, direction: "asc" });
										}
									}
								}}
								IconComponent={(props) => <TableSortLabelIcon {...props} direction={sortDirection ?? "asc"} />}
							>
								{headerContent}
							</TableSortLabel>
						</Tooltip>
					);
				}
				return (
					<CompositeTableHeaderCell
						key={key}
						{...tableCellProps}
						sx={{ width, ...sx }}
						aria-sort={sortDirection ? (sortDirection === "asc" ? "ascending" : "descending") : undefined}
					>
						{headerContent}
					</CompositeTableHeaderCell>
				);
			})}
		</TableRow>
	);
}

export const CompositeTableBodyRow = forwardRef<HTMLTableRowElement, Omit<TableRowProps, "onKeyDown">>(
	function SessionTableBodyRow({ onClick, children, sx, role, ...tableRowProps }, ref) {
		return (
			<TableRow
				hover
				{...tableRowProps}
				role={role ?? (onClick ? "button" : undefined)}
				onClick={onClick}
				tabIndex={onClick ? 0 : undefined}
				onKeyDown={
					onClick
						? (e) => {
								if (e.key === "Enter" || e.key === " ") {
									onClick(e as unknown as React.MouseEvent<HTMLTableRowElement>);
								}
							}
						: undefined
				}
				sx={{
					...sx,
					cursor: onClick ? "pointer" : undefined,
					"&:focus-visible": onClick
						? {
								outline: "2px solid #0b6f85",
								outlineOffset: "-2px",
							}
						: undefined,
				}}
				ref={ref}
			>
				{children}
			</TableRow>
		);
	},
);

export function CompositeTableCell({ className, children, ...tableCellProps }: TableCellProps) {
	return (
		<TableCell {...tableCellProps} className={`${styles.tableCell} ${className || ""}`}>
			{children}
		</TableCell>
	);
}

export function CompositeTablePaginationWrapper({
	children,
	className,
	tableWrapperClassName,
	page,
	onChange,
	count,
}: {
	children: ReactNode;
	className?: string;
	tableWrapperClassName?: string;
	page: number;
	onChange: (newPage: number) => void;
	count: number;
}) {
	return (
		<div className={`${styles.paginationWrapper} ${className || ""}`}>
			<CompositeTableContainer className={tableWrapperClassName}>{children}</CompositeTableContainer>
			<CorePagination
				page={page + 1}
				onChange={(_, newPage) => onChange(newPage - 1)}
				count={count}
				className={styles.pagination}
			/>
		</div>
	);
}

export function CompositeTableNoResults({ header, children }: { header?: ReactNode; children: ReactNode }) {
	return (
		<div className={styles.noResultsWrapper}>
			<CoreTableNoResultsIcon aria-hidden="true" style={{ width: "min(225px, 80%)", marginTop: "-50px" }} />
			{header && <Typography className={styles.noResultsHeader}>{header}</Typography>}
			{children}
		</div>
	);
}

export function CompositeTableSpinner() {
	return (
		<div className={styles.spinnerWrapper}>
			<CoreSpinner size={100} />
		</div>
	);
}

export function CompositeTableCellSkeleton(tableCellProps: Omit<TableCellProps, "children">) {
	return (
		<CompositeTableCell {...tableCellProps}>
			<Skeleton variant="text" animation="wave" width="100%" />
		</CompositeTableCell>
	);
}

export function CompositeTableSkeleton({
	columns,
	rows = 5,
	"aria-label": ariaLabel,
}: {
	columns: CompositeTableHeaderColumn<string, string>[];
	rows?: number;
	"aria-label"?: string;
}) {
	// The column names are placeholders too, so the header doesn't read as loaded while the body under it
	// is still filling in. Narrower than the body's bars because a heading is shorter than its column's
	// content. Two exceptions: a column with no label (spacers like a row-expand toggle) stays empty
	// rather than growing a heading it never has, and `sortable` is dropped along with the text — there
	// is nothing to sort yet, and the sort control would render on top of the placeholder.
	const skeletonColumns = columns.map(({ content, sortable: _sortable, ...column }) => ({
		...column,
		sortable: false as const,
		content:
			content === null || content === undefined || content === "" ? (
				content
			) : (
				<Skeleton variant="text" animation="wave" width="60%" />
			),
	}));

	return (
		<CompositeTable aria-label={ariaLabel} head={<CompositeTableHeaderRow columns={skeletonColumns} />}>
			{Array.from(new Array(rows), (_, index) => (
				<CompositeTableBodyRow key={index}>
					{columns.map(({ key, content: _content, sortable: _sortable, ...tableCellProps }) => (
						<CompositeTableCellSkeleton key={key} {...tableCellProps} />
					))}
				</CompositeTableBodyRow>
			))}
		</CompositeTable>
	);
}
